import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import SignoutHeader from "../subcomponents/SignoutHeader";
import { Colors } from "../Colors";
import axios from "axios";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { Insurance, InsuranceType, WEBPT_INSURANCE_TYPES } from "../types";
import { Chip, IconButton, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

interface EditableNumberCellProps {
  value: number;
  onSave: (newValue: number) => void;
  align?: "left" | "right" | "center" | "inherit" | "justify";
}

const EditableNumberCell: React.FC<EditableNumberCellProps> = ({ value, onSave, align = "left" }) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [draftValue, setDraftValue] = React.useState(value.toString());

  const handleCancel = () => {
    setDraftValue(value.toString());
    setIsEditing(false);
  };

  const handleConfirm = () => {
    const parsed = parseInt(draftValue, 10);
    if (!isNaN(parsed)) {
      onSave(parsed);
    }
    setIsEditing(false);
  };

  return (
    <TableCell align={align}>
      {isEditing ? (
        <>
          <TextField type="number" size="small" value={draftValue} onChange={(e) => setDraftValue(e.target.value)} inputProps={{ min: 0 }} sx={{ width: 80, mr: 1 }} />
          <IconButton onClick={handleConfirm} size="small">
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={handleCancel} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      ) : (
        <>
          {value}
          <IconButton onClick={() => setIsEditing(true)} size="small" sx={{ ml: 1 }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </>
      )}
    </TableCell>
  );
};

const PayersPage: React.FC = () => {
  const [payers, setPayers] = React.useState<Insurance[]>([]);
  const [payerTypes, setPayerTypes] = React.useState<InsuranceType[]>([]);

  const user = useSelector((state: RootState) => state.auth.user);

  React.useEffect(() => {
    const fetchPayers = async () => {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/payers`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });
      const sortedPayers = response.data;
      setPayers(sortedPayers);
    };
    const fetchPayerTypes = async () => {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/payers/types`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });
      const data = response.data as InsuranceType[];
      setPayerTypes(data.sort((a, b) => parseInt(a.ehrInsuranceTypeId) - parseInt(b.ehrInsuranceTypeId)));
    };
    if (user?.token) {
      fetchPayers();
      fetchPayerTypes();
    }
  }, [user?.token]);

  const initializePayers = async () => {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/payers/initialize`,
      {},
      {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      },
    );
    console.log(response.data);
  };

  const clearPayers = async () => {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/payers/clear`,
      {},
      {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      },
    );
    console.log(response.data);
  };

  const updateInsuranceType = async (payerId: string, updatedData: Partial<InsuranceType>) => {
    setPayerTypes((prev) => prev.map((type) => (type.insuranceTypeId === payerId ? { ...type, ...updatedData } : type)));
    const response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/payers/types/${payerId}`, updatedData, {
      headers: {
        Authorization: `Bearer ${user?.token}`,
      },
    });
  };

  return (
    <Box>
      {/* payers table with id and name columns */}
      <SignoutHeader />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          marginTop: 2,
        }}
      >
        <Box display="flex" alignItems="center">
          <h1>Insurances</h1>
          <Chip
            label="Company-Wide"
            size="small"
            sx={{
              backgroundColor: `${Colors.info}20`,
              color: Colors.info,
              border: `1px solid ${Colors.info}40`,
              fontSize: "0.7rem",
              height: "20px",
              marginLeft: "8px",
            }}
          />
        </Box>
        <Button variant="contained" onClick={initializePayers}>
          Sync Insurances From WebPT
        </Button>

        <TableContainer component={Paper} sx={{ marginTop: 2, width: "fit-content", display: "inline-block" }}>
          <Table sx={{ width: "auto" }} size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>WEBPT Insurance Type</TableCell>
                <TableCell>Limit Per Hour</TableCell>
                <TableCell>Default Allowed Visits</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payerTypes.map((type) => (
                <TableRow key={type.ehrInsuranceTypeId}>
                  <TableCell>{type.ehrInsuranceTypeId}</TableCell>
                  <TableCell>{type.name}</TableCell>
                  <TableCell>
                    <EditableNumberCell value={type.limitPerHour ?? 0} onSave={(newValue) => updateInsuranceType(type.insuranceTypeId, { limitPerHour: newValue })} />
                  </TableCell>
                  <TableCell>
                    <EditableNumberCell value={type.defaultAllowedVisits ?? 0} onSave={(newValue) => updateInsuranceType(type.insuranceTypeId, { defaultAllowedVisits: newValue })} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            width: "100%",
            maxWidth: 800,
            bgcolor: Colors.bg,
            borderRadius: 2,
            boxShadow: 1,
            overflow: "auto",
          }}
        >
          <TableContainer component={Paper} sx={{ marginTop: 2 }}>
            <Table sx={{ minWidth: 650 }} size="small">
              <TableHead>
                <TableRow>
                  <TableCell>WEBPT ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payers.map((payer) => (
                  <TableRow key={payer.ehrInsuranceId}>
                    <TableCell>{payer.ehrInsuranceId}</TableCell>
                    <TableCell>{payer.ehrInsuranceTypeId && WEBPT_INSURANCE_TYPES[payer.ehrInsuranceTypeId as keyof typeof WEBPT_INSURANCE_TYPES]}</TableCell>
                    <TableCell>{payer.name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
};

export default PayersPage;
